import type { DivExtensionClass } from '@divkitframework/divkit/typings/common.d.ts';
import type { FunnelTranslations } from '@/modules/i18n';
import type { Logger } from '@/modules/logger';
import { createLocalizationExtensions } from '@/modules/divkit/localization';
import { createIntercomExtension } from './intercom';
import { getSafeSearchParams } from '@/config/searchParams';
import { captureCardVariables, resetCardVariables } from '@/modules/divkit/cardVariables';
import type { DivExtensionContext } from '@divkitframework/divkit/typings/common.d.ts';
import type { SsoRuntimeOptions } from './sso/types';
import type { PromoTimerRuntimeOptions } from './promo-timer';

export interface ExtensionRuntimeOptions {
  translations: FunnelTranslations;
  logger?: Logger;
  language?: string;
  /** Нужен только layout-ам с `sso_buttons`; без него extension монтируется без кнопок и пишет warning. */
  sso?: SsoRuntimeOptions;
  /** Paywall layouts with `promoTimer`; the host store owns the anchored countdown. */
  promoTimer?: PromoTimerRuntimeOptions;
}

type LazyExtensionLoader = (options: ExtensionRuntimeOptions) => Promise<DivExtensionClass>;

class FullHeightExtension {
  mountView(node: HTMLElement, context: DivExtensionContext): void {
    node.style.minHeight = '100vh';
    // rootState несёт full_height во всех layout — здесь шелл получает доступ к card-переменным.
    captureCardVariables(context);
  }

  unmountView(_node: HTMLElement, context: DivExtensionContext): void {
    resetCardVariables(context);
  }
}

class PageExtension {}

const lazyLoaders: Record<string, LazyExtensionLoader> = {
  lottie: async () => (await import('./lottie')).createLottieExtension(),
  sticky: async ({ logger }) => (await import('./sticky')).createStickyExtension(logger),
  bmi_slider: async ({ language, logger }) => (await import('./bmi-slider')).createBmiSliderExtension(language, logger),
  calendar: async ({ language, logger }) => (await import('./calendar')).createCalendarExtension(language, logger),
  countDown: async ({ logger }) => (await import('./countdown')).createCountDownExtension(logger),
  promoTimer: async ({ promoTimer, logger }) =>
    (await import('./promo-timer')).createPromoTimerExtension(promoTimer, logger),
  scratch: async ({ logger }) => (await import('./scratch')).createScratchExtension(logger),
  internal_modal: async ({ logger }) => (await import('./internal-modal')).createInternalModalExtension(logger),
  backdrop_blur: async ({ logger }) => (await import('./backdrop-blur')).createBackdropBlurExtension(logger),
  slide_up: async ({ logger }) => (await import('./slide-up')).createSlideUpExtension(logger),
  percentLoader: async ({ logger }) => (await import('./percent-loader')).createPercentLoaderExtension(logger),
  chat_feed: async ({ logger }) => (await import('./chat-feed')).createChatFeedExtension(logger),
  keyframes: async ({ logger }) => (await import('./keyframes')).createKeyframesExtension(logger),
  mask: async ({ logger }) => (await import('./mask')).createMaskExtension(logger),
  slider: async ({ logger }) => (await import('./slider')).createSliderExtension(logger),
  sso_buttons: async ({ logger, sso }) =>
    (await import('./sso')).createSsoButtonsExtension(
      sso ?? { clientIds: {}, onAuthorized: () => logger?.logWarning({}, 'ssoAuthorizedWithoutHandler') },
      logger,
    ),
};

export const registeredExtensionIds = [
  'full_height',
  'intercom',
  'localization',
  'page',
  'lottie',
  'sticky',
  'bmi_slider',
  'calendar',
  'countDown',
  'promoTimer',
  'scratch',
  'internal_modal',
  'backdrop_blur',
  'slide_up',
  'percentLoader',
  'sso_buttons',
  'chat_feed',
  'keyframes',
  'mask',
  'slider',
] as const;

const isDebug = (): boolean => {
  try {
    const value = getSafeSearchParams().get('debug');
    return value === '1' || value === 'true';
  } catch {
    return false;
  }
};

/** Base extensions are bound to a translations instance; with shared translations this is one map per app run. */
const baseCache = new WeakMap<FunnelTranslations, Map<string, DivExtensionClass>>();

const buildBaseExtensions = (translations: FunnelTranslations, logger?: Logger): Map<string, DivExtensionClass> => {
  // Missing texts are silent for users (layer fallback / empty) but reported once per key; debug shows `⚠ field`.
  const reported = new Set<string>();
  const extensions = createLocalizationExtensions(translations, {
    markMissing: isDebug(),
    onMissing: (info) => {
      const id = `${info.field ?? ''}|${info.priceId ?? ''}|${info.keys.join(',')}`;
      if (reported.has(id)) return;
      reported.add(id);
      logger?.logWarning(info, info.field ? 'tariffTextMissing' : 'localizationKeyMissing');
    },
  });
  extensions.set('full_height', FullHeightExtension as unknown as DivExtensionClass);
  extensions.set('page', PageExtension as unknown as DivExtensionClass);
  extensions.set('intercom', createIntercomExtension(logger));
  return extensions;
};

/** A copy of the memoised base map: callers extend it with lazy extensions without touching the shared one. */
export const createExtensions = (translations: FunnelTranslations, logger?: Logger): Map<string, DivExtensionClass> => {
  let base = baseCache.get(translations);
  if (!base) {
    base = buildBaseExtensions(translations, logger);
    baseCache.set(translations, base);
  }
  return new Map(base);
};

const loaderCache = new Map<string, Promise<DivExtensionClass>>();
/** Whole layout maps, per translations instance, keyed by language and id set. */
const layoutCache = new WeakMap<FunnelTranslations, Map<string, Promise<Map<string, DivExtensionClass>>>>();

const normalizeLanguage = (language: string | undefined): 'en' | 'ru' => {
  return language?.toLowerCase().startsWith('ru') ? 'ru' : 'en';
};

/**
 * Same translations, language and id set → the same map. Every route used to rebuild it on mount; a shared
 * runtime asks once per layout contract. Page-specific options (`sso`, `promoTimer`) are captured by the
 * first call, exactly as `loaderCache` already did for the lazy loaders.
 */
export const loadLayoutExtensions = (
  extensionIds: string[],
  options: ExtensionRuntimeOptions,
): Promise<Map<string, DivExtensionClass>> => {
  const language = normalizeLanguage(options.language);
  const uniqueIds = [...new Set(extensionIds)];
  const key = `${language}|${[...uniqueIds].sort().join(',')}`;
  let perTranslations = layoutCache.get(options.translations);
  if (!perTranslations) {
    perTranslations = new Map();
    layoutCache.set(options.translations, perTranslations);
  }
  let pending = perTranslations.get(key);
  if (!pending) {
    pending = buildLayoutExtensions(uniqueIds, language, options).catch((error: unknown) => {
      // A broken contract is not worth remembering: the next caller gets a fresh attempt and a fresh error.
      perTranslations?.delete(key);
      throw error;
    });
    perTranslations.set(key, pending);
  }
  return pending;
};

const buildLayoutExtensions = async (
  uniqueIds: string[],
  language: 'en' | 'ru',
  options: ExtensionRuntimeOptions,
): Promise<Map<string, DivExtensionClass>> => {
  const extensions = createExtensions(options.translations, options.logger);
  const missing: string[] = [];

  await Promise.all(
    uniqueIds.map(async (id) => {
      if (extensions.has(id)) return;
      const loader = lazyLoaders[id];
      if (!loader) {
        missing.push(id);
        return;
      }
      const cacheKey = `${id}:${language}`;
      let promise = loaderCache.get(cacheKey);
      if (!promise) {
        promise = loader({ ...options, language }).catch((error: unknown) => {
          // A failed chunk fetch must not be remembered, or every later layout inherits the rejection.
          loaderCache.delete(cacheKey);
          throw error;
        });
        loaderCache.set(cacheKey, promise);
      }
      extensions.set(id, await promise);
    }),
  );

  if (missing.length) {
    const message = `Unregistered DivKit extensions: ${missing.sort().join(', ')}`;
    options.logger?.logError(new Error(message), 'extensionContractFailed');
    throw new Error(message);
  }

  return extensions;
};

export const assertExtensionsRegistered = (
  extensionIds: string[],
  logger?: Logger,
  additionalIds: readonly string[] = [],
): void => {
  const registered = new Set<string>([...registeredExtensionIds, ...additionalIds]);
  const missing = extensionIds.filter((id) => !registered.has(id));
  if (missing.length) {
    logger?.logError(new Error(`Unregistered DivKit extensions: ${missing.join(', ')}`), 'extensionContractFailed');
    throw new Error(`Unregistered DivKit extensions: ${missing.join(', ')}`);
  }
};
